import { useState } from 'react';

import { ExternalLinkIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { selectAvailableLocalTools } from '@/redux/featureFlag/selectors';
import { BrainGuidelines, Tool } from '@/shared/types/brain';
import { LOCAL_TOOLS_DOCS_URL } from '@/util/constants';
import { isToolReferencedInGuidelines } from '@/util/guidelineToolReferences';

import { DisableToolWarningDialog } from './DisableToolWarningDialog';

type LocalToolDefinition = { tool: Tool; labelKey: string; descriptionKey: string };

const LOCAL_TOOLS: LocalToolDefinition[] = [
  {
    tool: Tool.TRIGGER_WORKFLOW,
    labelKey: 'local_tools.trigger_workflow',
    descriptionKey: 'local_tools.trigger_workflow_description',
  },
  {
    tool: Tool.COMPOSE_RICH_RESPONSE,
    labelKey: 'local_tools.compose_rich_response',
    descriptionKey: 'local_tools.compose_rich_response_description',
  },
  {
    tool: Tool.EXECUTE_CODE,
    labelKey: 'local_tools.execute_code',
    descriptionKey: 'local_tools.execute_code_description',
  },
  {
    tool: Tool.ASK_QUESTIONS,
    labelKey: 'local_tools.ask_questions',
    descriptionKey: 'local_tools.ask_questions_description',
  },
  {
    tool: Tool.VOICEMAIL_DETECTION,
    labelKey: 'local_tools.voicemail_detection',
    descriptionKey: 'local_tools.voicemail_detection_description',
  },
];

type LocalToolsTabProps = {
  guidelines: BrainGuidelines | undefined;
  canWrite: boolean;
  tools: Tool[] | undefined;
  onToggleTool: (tool: Tool, enabled: boolean) => void;
  onCleanupToolReferences: (tool: Tool) => void;
};

const LocalToolsTab = ({
  guidelines,
  canWrite,
  tools,
  onToggleTool,
  onCleanupToolReferences,
}: LocalToolsTabProps) => {
  const { t } = useTranslation();
  const [pendingDisable, setPendingDisable] = useState<Tool | null>(null);

  const availableTools = useSelector(selectAvailableLocalTools);
  const localTools = LOCAL_TOOLS.filter(({ tool }) => availableTools.includes(tool));

  const handleToggle = (tool: Tool, checked: boolean) => {
    if (!checked && isToolReferencedInGuidelines(guidelines, tool)) {
      setPendingDisable(tool);
      return;
    }
    onToggleTool(tool, checked);
  };

  const handleConfirmDisable = () => {
    if (pendingDisable) {
      onToggleTool(pendingDisable, false);
      onCleanupToolReferences(pendingDisable);
      setPendingDisable(null);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-6 px-6 pt-4 pb-6">
        <div className="flex w-full max-w-lg flex-col">
          <ContextualHelp title={t('local_tools.about_title')} name="about_local_tools">
            {t('local_tools.about_description')}
          </ContextualHelp>

          <div className="flex flex-col gap-4">
            <Switch
              variant="choice-card"
              switchPosition="right"
              label={t('local_tools.response_evaluator')}
              description={t('local_tools.response_evaluator_description')}
              checked
              disabled
            />

            {localTools.map(({ tool, labelKey, descriptionKey }) => (
              <Switch
                key={tool}
                variant="choice-card"
                switchPosition="right"
                label={t(labelKey)}
                description={t(descriptionKey)}
                checked={tools?.includes(tool) ?? false}
                disabled={!canWrite}
                onCheckedChange={(checked) => handleToggle(tool, checked)}
              />
            ))}

            <Button variant="ghost-neutral" asChild className="w-fit">
              <a href={LOCAL_TOOLS_DOCS_URL} target="_blank" rel="noopener noreferrer">
                {t('local_tools.learn_more')}
                <ExternalLinkIcon />
              </a>
            </Button>
          </div>
        </div>
      </div>

      <DisableToolWarningDialog
        open={!!pendingDisable}
        onClose={() => setPendingDisable(null)}
        onConfirm={handleConfirmDisable}
      />
    </>
  );
};

export default LocalToolsTab;
